import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_031c01f6-1122-4ae8-87ee-69acab3ae0f1/artifacts/kw4zjnmy_ava.jpeg";
const HERO_IMAGE = "https://images.unsplash.com/photo-1599325601183-042bed55081c";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Invalid reset link");
      navigate("/forgot-password");
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success("Password reset successful!");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to reset password";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen relative flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Andaman Beach" className="w-full h-full object-cover" />
        <div className="hero-overlay absolute inset-0"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="glass-card rounded-2xl p-8 sm:p-10 shadow-2xl border border-white/20">
          <div className="flex justify-center mb-6">
            <Link to="/home"><img src={LOGO_URL} alt="Andaman Avalon" className="h-16 w-auto" /></Link>
          </div>

          {done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-serif font-semibold text-primary mb-3">Password Reset!</h1>
              <p className="text-muted-foreground text-sm mb-6">
                Your password has been updated. You can now log in with your new password.
              </p>
              <Link to="/login">
                <Button data-testid="go-to-login-btn" className="bg-primary hover:bg-primary/90">Go to Login</Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h1 className="text-2xl font-serif font-semibold text-primary mb-2">Set New Password</h1>
                <p className="text-muted-foreground text-sm">Choose a strong password for your account</p>
              </div>

              <form onSubmit={handleSubmit} data-testid="reset-form" className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      type={showPassword ? "text" : "password"}
                      data-testid="reset-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="At least 6 characters"
                      className="pl-10 pr-10"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      type={showPassword ? "text" : "password"}
                      data-testid="reset-confirm"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      placeholder="Re-enter new password"
                      className="pl-10"
                    />
                  </div>
                </div>

                <Button type="submit" data-testid="reset-submit-btn" disabled={loading} className="w-full bg-primary hover:bg-primary/90 h-11">
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
